//@author travis
import { S25Util } from "../util/s25-util";
import { DataAccess } from "../dataaccess/data.access";
import { Timeout } from "../decorators/timeout.decorator";
import { Invalidate } from "../decorators/cache.decorator";
import { Image } from "../pojo/Image";
import { Cache } from "../decorators/cache.decorator";

export class ImageService {
    private static normalizeStrings(image: Image.ImagesI | undefined) {
        if (!image) return;
        if (S25Util.isDefined(image.image_name)) image.image_name = image.image_name.toString();
        if (S25Util.isDefined(image.image_desc)) image.image_desc = image.image_desc.toString();
    }

    @Timeout
    @Cache({ immutable: true, targetName: "ImageService" })
    public static getImages(type?: number | string): Promise<Image.ImagesI[] | undefined> {
        let url = "/images.json";
        url = url + (type ? "?image_type=" + type : "");
        return DataAccess.get(DataAccess.injectCaller(url, "ImageDao.getImages")).then(function (data) {
            data = data && data.images && data.images.image && S25Util.array.forceArray(data.images.image);
            if (data?.length) {
                data.forEach((image: Image.ImagesI) => ImageService.normalizeStrings(image));
                data.sort(S25Util.shallowSort("image_name"));
            }

            return data;
        });
    }

    /**
     * Fetches lightweight metadata for a single image (no binary data).
     * Uses /images.json which returns only descriptive fields — use this when
     * the image src is already known and only alt text / display name is needed.
     */
    @Timeout
    @Cache({ immutable: true, targetName: "ImageService" })
    public static getImageMeta(imageId: number | string): Promise<Image.ImagesI | undefined> {
        return DataAccess.get(
            DataAccess.injectCaller("/images.json?image_id=" + imageId, "ImageService.getImageMeta"),
        ).then(function (data) {
            const image = data?.images?.image;
            ImageService.normalizeStrings(image);
            return image;
        });
    }

    @Timeout
    public static async getImage(imageId: number | string): Promise<Image.ImageFullI | undefined> {
        const params = DataAccess.params({ image_id: imageId, caller: "ImageService.getImage" });
        const data = await DataAccess.get(`/image.json?${params}`);
        const image = data?.images?.image;
        ImageService.normalizeStrings(image);
        return image;
    }

    @Timeout
    @Invalidate({ serviceName: "S25ImageService", methodName: "getUrl", patternFunc: (args: any) => args[0] })
    @Invalidate({
        serviceName: "S25ImageService",
        methodName: "getUrlBootstrapWidth",
        patternFunc: (args: any) => args[0],
    })
    @Invalidate({ serviceName: "S25ImageService", methodName: "getLoaded", patternFunc: (args: any) => args[0] })
    @Invalidate({
        serviceName: "S25ImageService",
        methodName: "getLoadedBootstrapWidth",
        patternFunc: (args: any) => args[0],
    })
    @Invalidate({ serviceName: "S25ImageService", methodName: "getImages" })
    public static setImage(
        imageId: number,
        imageName: string,
        imageDesc: string,
        imageFileName: string,
        imageData: any,
        imageType?: number,
    ) {
        if (imageId) {
            return ImageService.getImage(imageId).then(function (data) {
                imageName = imageName ? imageName : (data?.image_name ?? "");
                imageDesc = imageDesc ? imageDesc : (data?.image_desc ?? "");
                imageType = imageType ? imageType : (data?.image_type ?? 1);
                imageFileName = imageFileName ? imageFileName : (data?.file_name ?? "");

                //Sending an empty imageData node breaks the image.
                if (imageData && imageData.indexOf("http") > -1) {
                    imageData = data?.image_data ?? "";
                } else {
                    imageData = S25Util.extractBase64FromURI(imageData);
                }

                return ImageService.putImage(imageId, imageName, imageDesc, imageFileName, imageData, imageType);
            });
        } else {
            return ImageService.postImage(
                imageName,
                imageDesc,
                imageFileName,
                S25Util.extractBase64FromURI(imageData),
                imageType,
            );
        }
    }

    @Timeout
    public static putImage(
        imageId: number,
        imageName: string,
        imageDesc: string,
        imageFileName: string,
        imageData: any,
        image_type?: number,
    ) {
        const payLoad = {
            images: {
                image: {
                    image_id: imageId,
                    image_name: imageName,
                    image_type: image_type || 1,
                    image_desc: imageDesc,
                    file_name: imageFileName,
                    image_data: imageData,
                },
            },
        };
        // //todo: fix image.json PUT and use that
        return DataAccess.put("/image.xml?image_id=" + imageId, S25Util.s25Json2Xml(payLoad)).then(function () {
            return {
                imageId: imageId,
            };
        });
    }

    @Timeout
    public static postImage(
        imageName: string,
        imageDesc: string,
        imageFileName: string,
        imageData: string,
        image_type?: number,
    ) {
        return DataAccess.post("/images.json").then(function (data) {
            const imageId = S25Util.propertyGetVal(data, "image_id");
            if (S25Util.isDefined(imageId)) {
                return ImageService.putImage(imageId, imageName, imageDesc, imageFileName, imageData, image_type).then(
                    function () {
                        return imageId;
                    },
                );
            }
        });
    }

    @Timeout
    public static deleteImage(imageId: number) {
        if (imageId) {
            return DataAccess.delete(
                DataAccess.injectCaller("/image.json?image_id=" + imageId, "ImageService.deleteImage"),
            ).then((resp) => {
                return resp;
            });
        } else {
            return S25Util.all({});
        }
    }
}
